import axios from "axios"

const RULE_AGENT_BASE_URL = process.env.REACT_APP_RULE_AGENT_URL || "http://localhost:9001"

const RULE_AGENT_STREAM_IDLE_TIMEOUT_MS = 120000

export const RESUMO_ALTERACAO_SENTINEL = "RESUMO_ALTERACAO:"

export const RULE_DESCRIPTION_MAX_LENGTH = 255

export const PREVIOUS_RULE_ATTACHMENT_LABEL = "Versão anterior da regra"

export async function openRuleAgentSession({ token, signal }) {
    let response = await fetch(RULE_AGENT_BASE_URL + "/session", {
        method: "POST",
        headers: {
            "Authorization": "Bearer " + token
        },
        signal
    })
    if (!response.ok) {
        throw new Error("Failed to open rule agent session: " + response.status)
    }
    let data = await response.json()
    return data.id
}

export function cancelRuleAgentTurn({ sessionId, token }) {
    return fetch(RULE_AGENT_BASE_URL + "/session/" + sessionId + "/turn/cancel", {
        method: "POST",
        headers: {
            "Authorization": "Bearer " + token
        }
    }).catch(() => {})
}

const RULE_PIECE_REVIEW_NOTE =
    "ATENÇÃO: o que está sendo salvo é um TRECHO DE REGRA (rule piece) — um fragmento reutilizável embutido em outras regras. " +
    "Ele não precisa ser uma regra completa: não exija decisão final nem cobertura de todos os casos, e não trate a ausência disso como problema. " +
    "Avalie apenas a coerência interna do trecho, lembrando que uma decisão dentro do trecho interrompe toda regra que o utiliza.\n\n"

export function buildRuleChangePrompt(options) {
    let isRulePiece = (options || {}).isRulePiece
    return (
        (isRulePiece
            ? "Você está analisando uma alteração feita em um trecho de regra reutilizável (rule piece) do QI Tech Rule Engine que o usuário está prestes a salvar.\n\n" + RULE_PIECE_REVIEW_NOTE
            : "Você está analisando uma alteração feita em uma regra do QI Tech Rule Engine que o usuário está prestes a salvar.\n\n") +
        (isRulePiece
            ? "A versão ATUAL do trecho (após a alteração) está na seção de contexto desta mensagem. "
            : "A versão ATUAL da regra (após a alteração) está na seção de contexto desta mensagem. ") +
        "A versão ANTERIOR (antes da alteração) está no trecho destacado \"" + PREVIOUS_RULE_ATTACHMENT_LABEL + "\".\n\n" +
        "Explique em português o que mudou entre as duas versões, estruturando sua resposta assim:\n" +
        "1. Um resumo curto (1 a 3 frases) do que mudou e do impacto no comportamento da regra — visível de imediato.\n" +
        "2. Uma tabela de alterações em markdown (colunas: Validação | O que mudou | Antes | Depois; use \"—\" quando um lado não existir). Uma linha por alteração concreta, células curtas.\n" +
        "3. Somente se houver uma explicação longa e detalhada, coloque-a dentro de um bloco :::detalhes Explicação detalhada ... ::: (o dashboard renderiza como seção expansível). Se a explicação for curta, não use o bloco.\n\n" +
        "Se identificar problemas na regra resultante, além de citá-los no texto, reporte-os com a ferramenta report_rule_warnings (severity 'critical' para bugs que quebram a regra ou geram comportamento perigoso; 'warning' para possíveis problemas ou riscos). Não chame a ferramenta se a regra estiver correta.\n\n" +
        "IMPORTANTE: primeiro conclua todas as chamadas de ferramentas, sem escrever texto junto delas; depois escreva a explicação COMPLETA em uma única mensagem final. Texto escrito antes ou entre ferramentas é descartado e nunca chega ao usuário.\n\n" +
        "Ao final da sua resposta, inclua obrigatoriamente uma última linha (fora de qualquer bloco :::detalhes), exatamente no seguinte formato:\n" +
        RESUMO_ALTERACAO_SENTINEL + " <resumo da alteração em uma frase, adequado para um changelog>\n" +
        "Essa última linha vai direto para o campo de descrição da regra, que aceita no máximo " +
        RULE_DESCRIPTION_MAX_LENGTH + " caracteres. O texto depois do prefixo precisa caber nesse limite: escreva uma única frase objetiva, mirando em torno de 150 caracteres, sem lista, sem quebra de linha e sem markdown."
    )
}

export function buildNewRulePrompt(options) {
    let isRulePiece = (options || {}).isRulePiece
    return (
        (isRulePiece
            ? "Você está analisando um NOVO trecho de regra reutilizável (rule piece) do QI Tech Rule Engine que o usuário acabou de montar e está prestes a salvar pela primeira vez (o trecho está na seção de contexto desta mensagem).\n\n" + RULE_PIECE_REVIEW_NOTE +
              "Explique em português o que o trecho faz e aponte possíveis problemas internos (validações mortas, inconsistências, comparações invertidas), estruturando sua resposta assim:\n"
            : "Você está analisando uma NOVA regra do QI Tech Rule Engine que o usuário acabou de montar e está prestes a salvar pela primeira vez (a regra está na seção de contexto desta mensagem).\n\n" +
              "Explique em português o que a regra faz e aponte possíveis problemas (validações mortas, decisões faltando, inconsistências, ordem incorreta), estruturando sua resposta assim:\n") +
        "1. Um resumo curto (1 a 3 frases) do que a regra faz — visível de imediato.\n" +
        "2. Problemas encontrados, se houver — sempre visíveis, nunca dentro de bloco expansível.\n" +
        "3. Somente se a explicação validação por validação for longa, coloque-a dentro de um bloco :::detalhes Explicação detalhada ... ::: (o dashboard renderiza como seção expansível). Se for curta, não use o bloco.\n\n" +
        "Se identificar problemas, além de citá-los no texto, reporte-os com a ferramenta report_rule_warnings (severity 'critical' para bugs que quebram a regra ou geram comportamento perigoso; 'warning' para possíveis problemas ou riscos). Não chame a ferramenta se a regra estiver correta.\n\n" +
        "IMPORTANTE: primeiro conclua todas as chamadas de ferramentas, sem escrever texto junto delas; depois escreva a explicação COMPLETA em uma única mensagem final. Texto escrito antes ou entre ferramentas é descartado e nunca chega ao usuário.\n\n" +
        "Ao final da sua resposta, inclua obrigatoriamente uma última linha (fora de qualquer bloco :::detalhes), exatamente no seguinte formato:\n" +
        RESUMO_ALTERACAO_SENTINEL + " <descrição do que a regra faz em uma frase, adequada para o campo de descrição da regra>\n" +
        "Essa última linha vai direto para o campo de descrição da regra, que aceita no máximo " +
        RULE_DESCRIPTION_MAX_LENGTH + " caracteres. O texto depois do prefixo precisa caber nesse limite: escreva uma única frase objetiva, mirando em torno de 150 caracteres, sem lista, sem quebra de linha e sem markdown."
    )
}

function resolveCurrentEventTypePayload(rulesContext) {
    if (!rulesContext.event_type || rulesContext.isRulePiece) return null
    let companies = Array.isArray(rulesContext.company) ? rulesContext.company : [rulesContext.company]
    let matches = []
    companies.forEach((company) => {
        if (!company) return
        (company.company_events || []).forEach((companyEvent) => {
            let eventType = (companyEvent.field_mapping || {}).event_type
            if (eventType && eventType.enum === rulesContext.event_type) {
                matches.push(eventType)
            }
        })
    })
    if (matches.length === 0) return null
    let match = matches.filter((eventType) => eventType.environment === rulesContext.environment)[0] || matches[0]
    return { ...match, field_mappings: rulesContext.fields_mapper_list || [] }
}

function normalizeCompanyKey(companyKey) {
    return typeof companyKey === "string" && companyKey.trim() ? companyKey : null
}

export function buildCompanyEventPayload(rulesContext) {
    if (!rulesContext) return null
    let payload = {
        boxes: rulesContext.boxes || {},
        field_mapping: { mappings: rulesContext.fields_mapper_list || [] },
        processors_variables: rulesContext.processors_variables || [],
        signals: rulesContext.signals_list || [],
        scripts: rulesContext.scripts_list || [],
        lists: (rulesContext.lists || []).map((list) => ({
            list_key: list.list_key,
            name: list.name,
            fields: (list.fields || []).map((field) => ({
                name: field.name,
                type: (field.type || {}).enum
            }))
        }))
    }
    let currentEventType = resolveCurrentEventTypePayload(rulesContext)
    if (currentEventType) payload.event_type = currentEventType
    let companyKey = normalizeCompanyKey(rulesContext.company_key)
    if (companyKey) payload.company_key = companyKey
    return payload
}

export function collectRulePieceKeys(wireTreeData) {
    let keys = []
    let visit = (nodes) => {
        (nodes || []).forEach((node) => {
            if (node.type === "rule_piece_box") {
                let key = (node.properties || {}).rule_piece_key
                if (key && !keys.includes(key)) keys.push(key)
            }
            visit(node.children)
        })
    }
    visit(wireTreeData)
    return keys
}

export function fetchRulePieceDefinitions({ keys, environment, companyKey }) {
    let params = {}
    if (environment) params.environment = environment
    let config = { params }
    let normalizedCompanyKey = normalizeCompanyKey(companyKey)
    if (normalizedCompanyKey) config.headers = { "COMPANY-KEY": normalizedCompanyKey }
    let requests = (keys || []).map((key) =>
        axios.get("/dash/rule_engine/rule_piece/" + key, config)
            .then((response) => ({
                rule_piece_key: key,
                name: response.data.name,
                tree_data: response.data.tree_data
            }))
            .catch((error) => {
                console.warn("Rule piece definition fetch failed for " + key + ":", error)
                return null
            })
    )
    return Promise.all(requests).then((definitions) =>
        definitions.filter((definition) =>
            definition && Array.isArray(definition.tree_data) && definition.tree_data.length > 0
        )
    )
}

export async function streamRuleChangeTurn({ sessionId, token, prompt, ruleContext, rulePieces, companyEvent, attachments, signal, onDelta, onRule, onWarnings, onDone, onError }) {
    let localController = new AbortController()
    let timedOut = false

    let forwardAbort = () => localController.abort()
    if (signal) {
        if (signal.aborted) localController.abort()
        else signal.addEventListener("abort", forwardAbort)
    }

    let timeoutId = null
    let armIdleTimeout = () => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
            timedOut = true
            localController.abort()
        }, RULE_AGENT_STREAM_IDLE_TIMEOUT_MS)
    }
    armIdleTimeout()

    let cleanup = () => {
        clearTimeout(timeoutId)
        if (signal) signal.removeEventListener("abort", forwardAbort)
    }

    let payload = { prompt }
    if (ruleContext) payload.rule_context = ruleContext
    if (rulePieces && rulePieces.length > 0) payload.rule_pieces = rulePieces
    if (companyEvent) payload.company_event = companyEvent
    if (attachments && attachments.length > 0) payload.attachments = attachments

    try {
        let response = await fetch(RULE_AGENT_BASE_URL + "/session/" + sessionId + "/turn/stream", {
            method: "POST",
            headers: {
                "Authorization": "Bearer " + token,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload),
            signal: localController.signal
        })

        if (!response.ok || !response.body) {
            cleanup()
            onError && onError({ message: "Failed to start rule agent stream: " + response.status })
            return
        }

        let reader = response.body.getReader()
        let decoder = new TextDecoder()
        let buffer = ""

        while (true) {
            let { value, done } = await reader.read()
            if (done) break
            armIdleTimeout()
            buffer += decoder.decode(value, { stream: true })

            let frames = buffer.split("\n\n")
            buffer = frames.pop()

            for (let frame of frames) {
                parseRuleAgentFrame(frame, { onDelta, onRule, onWarnings, onDone, onError })
            }
        }

        if (buffer.trim()) {
            parseRuleAgentFrame(buffer, { onDelta, onRule, onWarnings, onDone, onError })
        }
        cleanup()
    } catch (error) {
        cleanup()
        if (timedOut) {
            onError && onError({ message: "Rule agent stream timed out" })
            return
        }
        if (error.name === "AbortError") return
        onError && onError({ message: error.message })
    }
}

export const streamRuleAgentTurn = streamRuleChangeTurn

export function getRuleAgentDisplayText(fullText) {
    let sentinelIndex = fullText.indexOf(RESUMO_ALTERACAO_SENTINEL)
    if (sentinelIndex !== -1) {
        return fullText.slice(0, sentinelIndex).trim()
    }

    let maxOverlap = Math.min(RESUMO_ALTERACAO_SENTINEL.length - 1, fullText.length)
    for (let overlap = maxOverlap; overlap > 0; overlap--) {
        if (fullText.slice(-overlap) === RESUMO_ALTERACAO_SENTINEL.slice(0, overlap)) {
            return fullText.slice(0, fullText.length - overlap)
        }
    }

    return fullText
}

export function extractRuleChangeSummary(fullText) {
    let sentinelIndex = fullText.indexOf(RESUMO_ALTERACAO_SENTINEL)
    if (sentinelIndex === -1) return null
    let summary = fullText.slice(sentinelIndex + RESUMO_ALTERACAO_SENTINEL.length).trim()
    return summary.length > 0 ? summary : null
}

const DESCRIPTION_ELLIPSIS = "\u2026"

export function fitRuleDescription(summary) {
    if (!summary) return summary
    let flattened = summary.replace(/\s+/g, " ").trim()
    if (flattened.length <= RULE_DESCRIPTION_MAX_LENGTH) return flattened

    let budget = RULE_DESCRIPTION_MAX_LENGTH - DESCRIPTION_ELLIPSIS.length
    let cut = flattened.slice(0, budget)
    let lastSpace = cut.lastIndexOf(" ")
    if (lastSpace > budget - 40) cut = cut.slice(0, lastSpace)
    return cut.replace(/[\s.,;:—-]+$/, "") + DESCRIPTION_ELLIPSIS
}

function parseRuleAgentFrame(frame, { onDelta, onRule, onWarnings, onDone, onError }) {
    let eventType = "message"
    let dataLines = []

    frame.split("\n").forEach((line) => {
        if (line.startsWith("event:")) {
            eventType = line.slice("event:".length).trim()
        } else if (line.startsWith("data:")) {
            dataLines.push(line.slice("data:".length).trim())
        }
    })

    if (dataLines.length === 0) return

    let rawData = dataLines.join("\n")
    let data
    try {
        data = JSON.parse(rawData)
    } catch (parseError) {
        console.error("Failed to parse rule agent SSE frame data:", rawData, parseError)
        return
    }

    if (eventType === "delta") {
        onDelta && onDelta(data)
    } else if (eventType === "rule") {
        onRule && onRule(data)
    } else if (eventType === "warnings") {
        onWarnings && onWarnings(data)
    } else if (eventType === "done") {
        onDone && onDone(data)
    } else if (eventType === "error") {
        onError && onError(data)
    }
}
